import React, {
    useEffect,
    useMemo,
    useState
}
from "react";

import { useHistory }
from "react-router-dom";

import { FontAwesomeIcon }
from "@fortawesome/react-fontawesome";

import {
    faChevronRight,
    faRotateLeft,
    faSpinner,
    faXmark
}
from "@fortawesome/free-solid-svg-icons";

import DashboardInsightResultsDrawer
from "./DashboardInsightResultsDrawer";


export default function DashboardInsightDrawer({
    display,
    title,
    insights = [],
    loading = false,
    closeFunction,
    viewInsightsFunction
})
{

	const history =
		    useHistory();

		
		
	
	const restoredState =
	    history.location.state
	    &&
	    history.location.state.insightExplorer
	        ?
	        history.location.state.insightExplorer
	        :
	        null;
			
	const [
			    showResultsDrawer,
			    setShowResultsDrawer
	] =
	    useState(
	        restoredState
	            ?
	            restoredState.resultsDrawerOpen
	            :
	            false
	    );
		
		
	function viewInsightsDrawer()
	{
	    setShowResultsDrawer(
	        true
	    );
	}
	
    //--------------------------------------------------------
    // Filters
    //--------------------------------------------------------

	const [filters, setFilters] =
	    useState(
	        restoredState
	            ?
	            restoredState.filters
	            :
	            {}
	    );


    //--------------------------------------------------------
    // Reset filters when a new category is opened
    //--------------------------------------------------------

	useEffect(
	    () =>
	    {
	        if(
	            display
	            &&
	            !restoredState
	        )
	        {
	            setFilters({});
	        }
	    },
	    [
	        display,
	        title
	    ]
	);


    //--------------------------------------------------------
    // Filter definitions
    //--------------------------------------------------------

    const filterDefinitions =
    [
        {
            key: "subcategory",
            label: "Subcategory"
        },
        {
            key: "severity",
            label: "Severity"
        },
        {
            key: "complianceStatus",
            label: "Compliance"
        },
        {
            key: "urgency",
            label: "Urgency"
        },
        {
            key: "riskProbability",
            label: "Risk Probability"
        },
        {
            key: "riskConsequence",
            label: "Risk Consequence"
        },
        {
            key: "businessPriority",
            label: "Business Priority"
        },
        {
            key: "operationalDisruption",
            label: "Operational Disruption"
        },
        {
            key: "status",
            label: "Status"
        }
    ];


    //--------------------------------------------------------
    // Apply filters
    //
    // OR within one dimension.
    // AND between dimensions.
    //
    // Example:
    //
    // Severity = LOW OR MEDIUM
    // AND
    // Compliance = POTENTIAL CONCERN
    //--------------------------------------------------------

    const filteredInsights =
        useMemo(
            () =>
            {
                return insights.filter(
                    (insight) =>
                    {
                        return Object.keys(filters).every(
                            (key) =>
                            {
                                const selectedValues =
                                    filters[key]
                                    ||
                                    [];


                                if(selectedValues.length === 0)
                                {
                                    return true;
                                }


                                return selectedValues.some(
                                    (selectedValue) =>
                                    {
                                        return (
                                            normaliseValue(
                                                insight[key]
                                            )
                                            ===
                                            normaliseValue(
                                                selectedValue
                                            )
                                        );
                                    }
                                );
                            }
                        );
                    }
                );
            },
            [
                insights,
                filters
            ]
        );


    //--------------------------------------------------------
    // Build breakdowns
    //
    // IMPORTANT:
    //
    // When calculating a dimension we ignore that
    // dimension's own active selections.
    //
    // This allows additional values within the same
    // dimension to remain selectable.
    //
    // Example:
    //
    // Severity LOW selected.
    //
    // Severity table still shows MEDIUM / HIGH etc,
    // but their counts respect all OTHER filters.
    //--------------------------------------------------------

    const breakdowns =
        useMemo(
            () =>
            {
                const result = {};


                filterDefinitions.forEach(
                    (definition) =>
                    {
                        const relevantInsights =
                            insights.filter(
                                (insight) =>
                                {
                                    return Object.keys(filters).every(
                                        (key) =>
                                        {
                                            //------------------------------------------------
                                            // Ignore this dimension's own filter
                                            //------------------------------------------------

                                            if(
                                                key
                                                ===
                                                definition.key
                                            )
                                            {
                                                return true;
                                            }


                                            const selectedValues =
                                                filters[key]
                                                ||
                                                [];


                                            if(selectedValues.length === 0)
                                            {
                                                return true;
                                            }


                                            return selectedValues.some(
                                                (selectedValue) =>
                                                {
                                                    return (
                                                        normaliseValue(
                                                            insight[key]
                                                        )
                                                        ===
                                                        normaliseValue(
                                                            selectedValue
                                                        )
                                                    );
                                                }
                                            );
                                        }
                                    );
                                }
                            );


                        const counts = {};


                        relevantInsights.forEach(
                            (insight) =>
                            {
                                const rawValue =
                                    insight[
                                        definition.key
                                    ];

                                const value =
                                    formatValue(
                                        rawValue
                                    );


                                counts[value] =
                                    (counts[value] || 0)
                                    +
                                    1;
                            }
                        );


                        result[
                            definition.key
                        ] =
                            Object.keys(counts)
                                .map(
                                    (value) =>
                                    {
                                        return {
                                            value,
                                            total:
                                                counts[value]
                                        };
                                    }
                                )
                                .sort(
                                    (a, b) =>
                                        b.total
                                        -
                                        a.total
                                );
                    }
                );


                return result;
            },
            [
                insights,
                filters
            ]
        );


    //--------------------------------------------------------
    // Select / deselect filter value
    //--------------------------------------------------------

    function selectFilter(
        key,
        value)
    {
        setFilters(
            (current) =>
            {
                const currentValues =
                    current[key]
                    ||
                    [];


                const alreadySelected =
                    currentValues.some(
                        (currentValue) =>
                        {
                            return (
                                normaliseValue(
                                    currentValue
                                )
                                ===
                                normaliseValue(
                                    value
                                )
                            );
                        }
                    );


                //------------------------------------------------
                // Remove existing selection
                //------------------------------------------------

                if(alreadySelected)
                {
                    const nextValues =
                        currentValues.filter(
                            (currentValue) =>
                            {
                                return (
                                    normaliseValue(
                                        currentValue
                                    )
                                    !==
                                    normaliseValue(
                                        value
                                    )
                                );
                            }
                        );


                    const next = {
                        ...current
                    };


                    if(nextValues.length === 0)
                    {
                        delete next[key];
                    }
                    else
                    {
                        next[key] =
                            nextValues;
                    }


                    return next;
                }


                //------------------------------------------------
                // Add selection
                //------------------------------------------------

                return {
                    ...current,

                    [key]:
                    [
                        ...currentValues,
                        value
                    ]
                };
            }
        );
    }


    //--------------------------------------------------------
    // Clear filters
    //--------------------------------------------------------

    function clearFilters()
    {
        setFilters({});
    }


    //--------------------------------------------------------
    // View insights
    //--------------------------------------------------------

    function viewInsights()
    {
        if(viewInsightsFunction)
        {
            viewInsightsFunction(
                filteredInsights
            );
        }
    }


    //--------------------------------------------------------
    // Active filter keys
    //--------------------------------------------------------

    const activeFilterKeys =
        Object.keys(filters)
            .filter(
                (key) =>
                {
                    return (
                        Array.isArray(filters[key])
                        &&
                        filters[key].length > 0
                    );
                }
            );


    //--------------------------------------------------------
    // Render
    //--------------------------------------------------------

    return (

        <>

            {/*----------------------------------------------*/}
            {/* BACKDROP                                     */}
            {/*----------------------------------------------*/}

            <div
                onClick={closeFunction}

                style={{
                    ...backdropStyle,

                    opacity:
                        display
                            ?
                            1
                            :
                            0,

                    pointerEvents:
                        display
                            ?
                            "auto"
                            :
                            "none"
                }}
            />


            {/*----------------------------------------------*/}
            {/* DRAWER                                       */}
            {/*----------------------------------------------*/}

            <div
                style={{
                    ...drawerStyle,

                    transform:
                        display
                            ?
                            "translateX(0)"
                            :
                            "translateX(100%)"
                }}
            >

                {/*------------------------------------------*/}
                {/* HEADER                                   */}
                {/*------------------------------------------*/}

                <div style={headerStyle}>

                    <div style={headerTextStyle}>

                        <div style={eyebrowStyle}>
                            Insight Explorer
                        </div>

                        <div style={titleStyle}>
                            {
                                title
                                ||
                                "Portfolio Insights"
                            }
                        </div>

                    </div>


                    <button
                        type="button"

                        style={closeButtonStyle}

                        onClick={
                            closeFunction
                        }
                    >
                        <FontAwesomeIcon
                            icon={faXmark}
                        />
                    </button>

                </div>


                {/*------------------------------------------*/}
                {/* CONTENT                                  */}
                {/*------------------------------------------*/}

                <div style={contentStyle}>

                    {
                        loading
                        ?
                            <div style={loadingStyle}>

                                <FontAwesomeIcon
                                    icon={faSpinner}
                                    spin
                                    style={loadingIconStyle}
                                />

                                <div style={loadingTitleStyle}>
                                    Loading insights
                                </div>

                                <div style={loadingTextStyle}>
                                    Building your insight view...
                                </div>

                            </div>
                        :
                            <>

                                {/*--------------------------------*/}
                                {/* FILTER / RESULT COMMAND PANEL  */}
                                {/*--------------------------------*/}

                                <div style={filterSummaryPanelStyle}>

                                    {/*----------------------------*/}
                                    {/* ACTIVE FILTERS              */}
                                    {/*----------------------------*/}

                                    <div style={filterSummaryLeftStyle}>

                                        <div style={filterSummaryHeadingStyle}>
                                            Active Filters
                                        </div>


                                        <div style={chipContainerStyle}>

                                            {/*--------------------*/}
                                            {/* BASE CATEGORY       */}
                                            {/*--------------------*/}

                                            {
                                                title
                                                &&
                                                <div style={categoryChipStyle}>
                                                    {title}
                                                </div>
                                            }


                                            {/*--------------------*/}
                                            {/* SELECTED FILTERS    */}
                                            {/*--------------------*/}

                                            {
                                                activeFilterKeys.map(
                                                    (key) =>
                                                    {
                                                        const definition =
                                                            filterDefinitions
                                                                .find(
                                                                    (item) =>
                                                                        item.key
                                                                        ===
                                                                        key
                                                                );


                                                        return (
                                                            filters[key]
                                                            ||
                                                            []
                                                        ).map(
                                                            (value) =>
                                                            (
                                                                <button
                                                                    key={
                                                                        key
                                                                        +
                                                                        "-"
                                                                        +
                                                                        value
                                                                    }

                                                                    type="button"

                                                                    style={
                                                                        filterChipStyle
                                                                    }

                                                                    onClick={() =>
                                                                    {
                                                                        selectFilter(
                                                                            key,
                                                                            value
                                                                        );
                                                                    }}
                                                                >

                                                                    <span>
                                                                        {
                                                                            definition
                                                                                ?
                                                                                definition.label
                                                                                +
                                                                                ": "
                                                                                :
                                                                                ""
                                                                        }

                                                                        {
                                                                            formatDisplayValue(
                                                                                value
                                                                            )
                                                                        }
                                                                    </span>


                                                                    <FontAwesomeIcon
                                                                        icon={
                                                                            faXmark
                                                                        }
                                                                    />

                                                                </button>
                                                            )
                                                        );
                                                    }
                                                )
                                            }

                                        </div>

                                    </div>


                                    {/*----------------------------*/}
                                    {/* RESULTS                     */}
                                    {/*----------------------------*/}

                                    <div style={filterSummaryRightStyle}>

                                        {
                                            activeFilterKeys.length > 0
                                            &&
                                            <button
                                                type="button"

                                                style={
                                                    darkResetButtonStyle
                                                }

                                                onClick={
                                                    clearFilters
                                                }
                                            >

                                                <FontAwesomeIcon
                                                    icon={
                                                        faRotateLeft
                                                    }
                                                />

                                                Reset

                                            </button>
                                        }


                                        <button
                                            type="button"

                                            style={
                                                insightCountButtonStyle
                                            }

                                            onClick={
                                                viewInsightsDrawer
                                            }
                                        >

                                            <div style={darkCountValueStyle}>
                                                {
                                                    filteredInsights
                                                        .length
                                                        .toLocaleString()
                                                }
                                            </div>


                                            <div style={darkCountTextStyle}>

                                                <div style={darkCountLabelStyle}>
                                                    Matching Insights
                                                </div>

                                                <div style={darkCountActionStyle}>

                                                    View insights

                                                    <FontAwesomeIcon
                                                        icon={
                                                            faChevronRight
                                                        }
                                                    />

                                                </div>

                                            </div>

                                        </button>

                                    </div>

                                </div>


                                {/*--------------------------------*/}
                                {/* FILTER TABLES                  */}
                                {/*--------------------------------*/}

                                <div style={breakdownGridStyle}>

                                    {
                                        filterDefinitions.map(
                                            (definition) =>
                                            {
                                                const rows =
                                                    breakdowns[
                                                        definition.key
                                                    ]
                                                    ||
                                                    [];


                                                if(rows.length === 0)
                                                {
                                                    return null;
                                                }


                                                return (

                                                    <FilterTable
                                                        key={
                                                            definition.key
                                                        }

                                                        definition={
                                                            definition
                                                        }

                                                        rows={
                                                            rows
                                                        }

                                                        selectedValues={
                                                            filters[
                                                                definition.key
                                                            ]
                                                            ||
                                                            []
                                                        }

                                                        selectFunction={
                                                            selectFilter
                                                        }
                                                    />

                                                );
                                            }
                                        )
                                    }

                                </div>

                            </>
                    }

                </div>

            </div>
			<DashboardInsightResultsDrawer
			    display={
			        showResultsDrawer
			    }

			    title={
			        title
			    }

			    insights={
			        filteredInsights
			    }

			    closeFunction={() =>
			    {
			        setShowResultsDrawer(
			            false
			        );
			    }}

			    insightClickFunction={(insight) =>
			    {
			        // SAVE OUR CURRENT EXPLORER STATE HERE

			        history.replace(
			            history.location.pathname,
			            {
			                insightExplorer:
			                {
			                    display: true,

			                    title:
			                        title,

			                    filters:
			                        filters,

			                    resultsDrawerOpen:
			                        true
			                }
			            }
			        );


			        // THEN NAVIGATE TO THE INSIGHT

			        history.push(
			            "/insight/"
			            +
			            insight.id
			        );
			    }}
			/>
        </>

    );
}


//--------------------------------------------------------
// Filter table
//--------------------------------------------------------

function FilterTable({
    definition,
    rows,
    selectedValues,
    selectFunction
})
{

    return (

        <div style={filterTableStyle}>

            {/*----------------------------------------------*/}
            {/* HEADER                                       */}
            {/*----------------------------------------------*/}

            <div style={filterTableHeaderStyle}>

                <div>
                    {definition.label}
                </div>

                <div>
                    Insights
                </div>

            </div>


            {/*----------------------------------------------*/}
            {/* ROWS                                         */}
            {/*----------------------------------------------*/}

            <div>

                {
                    rows.map(
                        (row) =>
                        {
                            const selected =
                                (selectedValues || [])
                                    .some(
                                        (selectedValue) =>
                                        {
                                            return (
                                                normaliseValue(
                                                    selectedValue
                                                )
                                                ===
                                                normaliseValue(
                                                    row.value
                                                )
                                            );
                                        }
                                    );


                            return (

                                <button
                                    key={
                                        definition.key
                                        +
                                        "-"
                                        +
                                        row.value
                                    }

                                    type="button"

                                    style={{
                                        ...filterRowStyle,

                                        ...(selected
                                            ?
                                            selectedFilterRowStyle
                                            :
                                            {})
                                    }}

                                    onClick={() =>
                                    {
                                        selectFunction(
                                            definition.key,
                                            row.value
                                        );
                                    }}
                                >

                                    <div style={filterValueStyle}>

                                        {
                                            formatDisplayValue(
                                                row.value
                                            )
                                        }

                                    </div>


                                    <div style={filterTotalContainerStyle}>

                                        <div
                                            style={{
                                                ...filterTotalStyle,

                                                ...(selected
                                                    ?
                                                    selectedFilterTotalStyle
                                                    :
                                                    {})
                                            }}
                                        >
                                            {
                                                row.total
                                                    .toLocaleString()
                                            }
                                        </div>


                                        <FontAwesomeIcon
                                            icon={
                                                faChevronRight
                                            }

                                            style={
                                                filterChevronStyle
                                            }
                                        />

                                    </div>

                                </button>

                            );
                        }
                    )
                }

            </div>

        </div>

    );
}


//--------------------------------------------------------
// Helpers
//--------------------------------------------------------

function normaliseValue(value)
{
    if(
        value === null
        ||
        value === undefined
    )
    {
        return "not assessed";
    }


    return String(value)
        .trim()
        .toLowerCase()
        .replace(/_/g, " ");
}


function formatValue(value)
{
    if(
        value === null
        ||
        value === undefined
        ||
        String(value).trim() === ""
    )
    {
        return "Not Assessed";
    }


    return String(value).trim();
}


function formatDisplayValue(value)
{
    const formatted =
        formatValue(value)
            .replace(/_/g, " ")
            .toLowerCase();


    return formatted.replace(
        /\b\w/g,
        (character) =>
            character.toUpperCase()
    );
}


//--------------------------------------------------------
// Backdrop
//--------------------------------------------------------

const backdropStyle =
{
    position: "fixed",

    inset: 0,

    zIndex: 1090,

    background:
        "rgba(15,23,42,0.18)",

    backdropFilter:
        "blur(2px)",

    transition:
        "opacity 0.35s ease"
};


//--------------------------------------------------------
// Drawer
//--------------------------------------------------------

const drawerStyle =
{
    position: "fixed",

    top: 0,
    right: 0,
    bottom: 0,

    /*
     * Leave roughly 90px of the application visible.
     */

    width: "calc(100vw - 90px)",
    height: "100vh",

    zIndex: 1100,

    display: "flex",
    flexDirection: "column",

    background:
        "rgba(250,251,253,0.985)",

    borderLeft:
        "1px solid rgba(148,163,184,0.30)",

    boxShadow:
        "-18px 0 50px rgba(15,23,42,0.16)",

    overflow: "hidden",

    transition:
        "transform 0.65s cubic-bezier(0.22, 1, 0.36, 1)"
};


//--------------------------------------------------------
// Header
//--------------------------------------------------------

const headerStyle =
{
    minHeight: "82px",

    display: "flex",
    alignItems: "center",

    padding: "0 30px",

    background:
        "rgba(255,255,255,0.96)",

    borderBottom:
        "1px solid #e7ebf0",

    flexShrink: 0
};


const headerTextStyle =
{
    flex: 1,

    minWidth: 0
};


const eyebrowStyle =
{
    color: "#477df5",

    fontSize: "10px",
    fontWeight: "800",

    letterSpacing: "1.4px",

    textTransform: "uppercase"
};


const titleStyle =
{
    marginTop: "4px",

    color: "#202936",

    fontSize: "20px",
    fontWeight: "750"
};


const closeButtonStyle =
{
    width: "38px",
    height: "38px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    color: "#667085",

    background: "#f4f6f8",

    border:
        "1px solid #e3e7ec",

    borderRadius: "10px",

    fontSize: "14px",

    cursor: "pointer"
};


//--------------------------------------------------------
// Content
//--------------------------------------------------------

const contentStyle =
{
    flex: 1,

    minHeight: 0,

    padding:
        "20px 30px 40px 30px",

    overflowY: "auto"
};


//--------------------------------------------------------
// Filter summary / command panel
//--------------------------------------------------------

const filterSummaryPanelStyle =
{
    minHeight: "82px",

    display: "flex",
    alignItems: "center",

    gap: "24px",

    padding: "15px 20px",

    background:
        "linear-gradient(to bottom, #30353c 0%, #1c2025 48%, #0f1114 100%)",

    border:
        "1px solid rgba(255,255,255,0.12)",

    borderRadius: "14px",

    boxShadow:
        "0 10px 26px rgba(0,0,0,0.22), "
        +
        "inset 0 1px 0 rgba(255,255,255,0.08)"
};


const filterSummaryLeftStyle =
{
    flex: 1,

    minWidth: 0
};


const filterSummaryHeadingStyle =
{
    marginBottom: "10px",

    color: "#8f9aaa",

    fontSize: "9px",
    fontWeight: "800",

    letterSpacing: "1.2px",

    textTransform: "uppercase"
};


const filterSummaryRightStyle =
{
    flexShrink: 0,

    display: "flex",
    alignItems: "center",

    gap: "18px"
};


//--------------------------------------------------------
// Chips
//--------------------------------------------------------

const chipContainerStyle =
{
    display: "flex",
    flexWrap: "wrap",

    gap: "8px"
};


const categoryChipStyle =
{
    padding: "7px 11px",

    color: "#ffffff",

    background:
        "linear-gradient(135deg, #4f8dff 0%, #2465ec 100%)",

    border:
        "1px solid rgba(110,174,255,0.45)",

    borderRadius: "16px",

    boxShadow:
        "0 4px 12px rgba(37,99,235,0.25)",

    fontSize: "10px",
    fontWeight: "750"
};


const filterChipStyle =
{
    display: "flex",
    alignItems: "center",

    gap: "7px",

    padding: "7px 11px",

    color: "#dbe5f3",

    background:
        "rgba(255,255,255,0.08)",

    border:
        "1px solid rgba(255,255,255,0.12)",

    borderRadius: "16px",

    fontSize: "10px",
    fontWeight: "700",

    cursor: "pointer"
};


//--------------------------------------------------------
// Count / action
//--------------------------------------------------------

const insightCountButtonStyle =
{
    display: "flex",
    alignItems: "center",

    gap: "12px",

    padding:
        "2px 0 2px 20px",

    color: "#ffffff",

    background:
        "transparent",

    border: "none",

    borderLeft:
        "1px solid rgba(255,255,255,0.12)",

    cursor: "pointer",

    textAlign: "left"
};


const darkCountValueStyle =
{
    color: "#6eaeff",

    fontSize: "30px",
    lineHeight: 1,

    fontWeight: "800",

    textShadow:
        "0 2px 12px rgba(71,125,245,0.22)"
};


const darkCountTextStyle =
{
    minWidth: "112px"
};


const darkCountLabelStyle =
{
    color: "#d4dae3",

    fontSize: "9px",
    fontWeight: "800",

    letterSpacing: "0.8px",

    textTransform: "uppercase"
};


const darkCountActionStyle =
{
    marginTop: "5px",

    display: "flex",
    alignItems: "center",

    gap: "6px",

    color: "#6eaeff",

    fontSize: "10px",
    fontWeight: "750"
};


const darkResetButtonStyle =
{
    display: "flex",
    alignItems: "center",

    gap: "6px",

    padding: "7px 10px",

    color: "#aeb8c6",

    background:
        "rgba(255,255,255,0.06)",

    border:
        "1px solid rgba(255,255,255,0.10)",

    borderRadius: "7px",

    fontSize: "9px",
    fontWeight: "750",

    cursor: "pointer"
};


//--------------------------------------------------------
// Breakdown grid
//--------------------------------------------------------

const breakdownGridStyle =
{
    marginTop: "16px",

    display: "grid",

    gridTemplateColumns:
        "repeat(3, minmax(0, 1fr))",

    gap: "14px",

    alignItems: "start"
};


//--------------------------------------------------------
// Filter table
//--------------------------------------------------------

const filterTableStyle =
{
    overflow: "hidden",

    background:
        "#ffffff",

    border:
        "1px solid #e5e9ef",

    borderRadius: "12px",

    boxShadow:
        "0 5px 18px rgba(29,42,62,0.045)"
};


const filterTableHeaderStyle =
{
    display: "grid",

    gridTemplateColumns:
        "1fr auto",

    gap: "10px",

    padding: "12px 14px",

    color: "#cbd5e1",

    background:
        "linear-gradient(180deg, #30353d 0%, #1d2127 100%)",

    borderBottom:
        "1px solid rgba(255,255,255,0.08)",

    fontSize: "9px",
    fontWeight: "800",

    letterSpacing: "0.8px",

    textTransform: "uppercase"
};


const filterRowStyle =
{
    width: "100%",

    minHeight: "39px",

    display: "flex",
    alignItems: "center",

    padding:
        "7px 12px 7px 14px",

    color: "#344054",

    background:
        "#ffffff",

    border: "none",

    borderBottom:
        "1px solid #f0f2f5",

    textAlign: "left",

    cursor: "pointer",

    transition:
        "all 0.15s ease"
};


const selectedFilterRowStyle =
{
    color: "#175cd3",

    background:
        "#eef5ff",

    boxShadow:
        "inset 3px 0 0 #477df5"
};


const filterValueStyle =
{
    flex: 1,

    minWidth: 0,

    overflow: "hidden",

    whiteSpace: "nowrap",

    textOverflow: "ellipsis",

    fontSize: "11px",
    fontWeight: "650"
};


const filterTotalContainerStyle =
{
    display: "flex",
    alignItems: "center",

    gap: "9px"
};


const filterTotalStyle =
{
    minWidth: "30px",

    padding: "4px 8px",

    color: "#526071",

    background:
        "#f1f4f7",

    borderRadius: "12px",

    textAlign: "center",

    fontSize: "10px",
    fontWeight: "800"
};


const selectedFilterTotalStyle =
{
    color: "#ffffff",

    background:
        "#477df5",

    boxShadow:
        "0 3px 8px rgba(71,125,245,0.25)"
};


const filterChevronStyle =
{
    color: "#a5afbc",

    fontSize: "9px"
};


//--------------------------------------------------------
// Loading
//--------------------------------------------------------

const loadingStyle =
{
    height: "100%",

    display: "flex",
    flexDirection: "column",

    alignItems: "center",
    justifyContent: "center",

    color: "#667085"
};


const loadingIconStyle =
{
    marginBottom: "14px",

    color: "#477df5",

    fontSize: "28px"
};


const loadingTitleStyle =
{
    color: "#344054",

    fontSize: "14px",
    fontWeight: "750"
};


const loadingTextStyle =
{
    marginTop: "5px",

    color: "#98a2b3",

    fontSize: "11px"
};